/**
 * Admin Portal Google sign-in — the parts of the redirect flow worth testing on their own.
 *
 * Who may sign in is decided by the SERVER (POST /api/admin/auth/google): a verified
 * Google Workspace account on coachcedar.com with a pre-created, active Admin Portal
 * account. Nothing here grants access. What this module adds (review of #4227, LOW-2):
 *
 *   * `state` — a random value generated when sign-in starts, kept on this device, and
 *     required back from Google on the callback. A callback this browser did not start
 *     (login CSRF: someone else's code delivered to our callback) is refused before the
 *     code is ever sent to the server.
 *   * `hd=coachcedar.com` — a hint so Google's account chooser offers Workspace accounts
 *     on our domain. A hint only; the server REQUIRES the id_token's hd claim.
 *   * no `access_type=offline` — we never used a Google refresh token, so we no longer
 *     ask for one.
 *
 * PKCE is not added: our OAuth client is confidential (the code is exchanged server-side
 * with the client secret), and a change to the token exchange cannot be verified against
 * Google before it ships. Listed as a follow-up.
 */

export const ADMIN_GOOGLE_DOMAIN = 'coachcedar.com';
export const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
export const ADMIN_GOOGLE_STATE_KEY = 'admin_google_oauth_state';
export const ADMIN_GOOGLE_CALLBACK_PATH = '/admin/auth/google/callback';

/** 32 random bytes as hex (256 bits). Throws if the browser has no CSPRNG — there is
 *  no non-cryptographic fallback, because a guessable state is no state at all. */
export function newOAuthState(
  cryptoImpl: { getRandomValues: (a: Uint8Array) => Uint8Array } | null | undefined =
    typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined,
): string {
  if (!cryptoImpl || typeof cryptoImpl.getRandomValues !== 'function') {
    throw new Error('Secure random numbers are unavailable in this browser');
  }
  const bytes = cryptoImpl.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function buildGoogleAuthUrl(clientId: string, origin: string, state: string): string {
  const url = new URL(GOOGLE_AUTH_ENDPOINT);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', `${origin}${ADMIN_GOOGLE_CALLBACK_PATH}`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('prompt', 'select_account');
  url.searchParams.set('hd', ADMIN_GOOGLE_DOMAIN);
  url.searchParams.set('state', state);
  return url.toString();
}

/** The callback may proceed only if Google returned exactly the state this device
 *  generated. A missing or empty value on either side is a mismatch. */
export function oauthStateMatches(
  expected: string | null | undefined,
  received: string | null | undefined,
): boolean {
  if (typeof expected !== 'string' || typeof received !== 'string') return false;
  if (expected.length === 0 || expected.length !== received.length) return false;
  let diff = 0;
  for (let i = 0; i < expected.length; i += 1) {
    diff |= expected.charCodeAt(i) ^ received.charCodeAt(i);
  }
  return diff === 0;
}
